import React, { useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';

import { getLinkAriaCurrent } from '../../currentNavItem';

import css from './PriorityLinks.module.css';

// The links are measured before the browser paints (backlog #4 C9): a menu remounted after the user
// loads then enters the topbar grouped in its first frame instead of an empty row first. The server
// renders no effects, so it keeps useEffect there (no useLayoutEffect warning).
const useMeasureEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// Task-creation contract (T16), the same as the client "Создать задачу" link in CustomLinksMenu:
// /l/draft/00000000-0000-0000-0000-000000000000/new/details?type=tasks
const CREATE_TASK_PARAMS = {
  slug: 'draft',
  id: '00000000-0000-0000-0000-000000000000',
  type: 'new',
  tab: 'details',
};

/**
 * Create component that shows only a single "Post a new listing" link. It is rendered only for the
 * client (showInlineCreateListing in TopbarDesktop), so it is the client's «Создать задачу» and uses
 * the task contract, not /l/new (T16).
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  const { customLinksMenuClass, postListingLabelId, categoriesCta } = props;
  return (
    <div className={customLinksMenuClass}>
      {categoriesCta}
      <NamedLink
        name="EditListingPage"
        params={CREATE_TASK_PARAMS}
        to={{ search: '?type=tasks' }}
        className={css.priorityLink}
      >
        <span className={classNames(css.priorityLinkLabel, css.secondaryCtaLabel)}>
          <FormattedMessage id={postListingLabelId} />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink, and
 * currentNav (the open page, backlog #9).
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig, currentNav }) => {
  const { text, type, href, route, highlight, secondaryCta } = linkConfig;
  const ariaCurrent = getLinkAriaCurrent(linkConfig, currentNav);
  const classes = classNames(css.priorityLink, {
    [css.highlight]: highlight,
    [css.currentLink]: ariaCurrent,
  });
  const labelClasses = classNames(css.priorityLinkLabel, {
    [css.secondaryCtaLabel]: secondaryCta,
  });
  const id = `priority-link-${text.toLowerCase().replace(/ /g, '-')}`;

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink
        name={name}
        params={params}
        to={to}
        className={classes}
        id={id}
        aria-current={ariaCurrent}
      >
        <span className={labelClasses}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={labelClasses}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array, priorityLinks, isGrouped, setLinks function and currentNav
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this effect, we measure the widths of each rendered priority link
  // This is done once before the real rendering and it's done outside the viewport.
  useMeasureEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks, isGrouped, currentNav } = props;
  const isServer = typeof window === 'undefined';
  // In the row only once grouped (CustomLinksMenu): until then they are measured off-screen, and
  // once measured they are not rendered at all until grouped.
  const isMeasured =
    isGrouped && links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  // Measured but not grouped yet, or never (below the desktop breakpoint the row is hidden and
  // gets no width): an empty row and no off-screen copy left in <body>, whose links stayed
  // focusable (backlog #4).
  if (!isServer && !isMeasured && links?.[0]?.width) {
    return <div className={css.priorityLinkWrapper} ref={containerRef} />;
  }

  return isMeasured || isServer ? (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return (
          <PriorityLink
            key={`${linkConfig.text}_${index}`}
            linkConfig={linkConfig}
            currentNav={currentNav}
          />
        );
      })}
    </div>
  ) : (
    ReactDOM.createPortal(
      <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
        {linkConfigs.map((linkConfig, index) => {
          return (
            <PriorityLink
              key={`${linkConfig.text}_${index}`}
              linkConfig={linkConfig}
              currentNav={currentNav}
            />
          );
        })}
      </div>,
      document.body
    )
  );
};

export default PriorityLinks;
